import { Decimal } from 'decimal.js';
import { parse as parseDate } from 'date-fns';
import * as _ from 'lodash';

// Nested attribute helpers
const getPathValue = (data, path) => {
  const parts = path.split('.');
  if (!parts) {
    return undefined;
  }

  let result = data;
  for (let p of parts) {
    result = result[p];

    if (_.isUndefined(result)) {
      break;
    }
  }

  return result;
};

const setPathValue = (data, path, value) => {
  const parts = path.split('.');
  if (!parts) {
    return;
  }

  let result = data;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];

    if (_.isUndefined(result[p])) {
      result[p] = {};
    } else if (!_.isObject(result[p])) {
      throw new Error('Invalid serialization path: ' + path);
    }

    result = result[p];
  }

  result[parts[parts.length - 1]] = value;
};

// Field types
export interface RuleType {
  load: (data: any) => any;
  save: (data: any) => any;
}

// Generic field without any value conversion
export function field(): RuleType {
  return {
    load: (data: any) => {
      return data;
    },
    save: (data: any) => {
      return data;
    }
  }
}

// Object field
export interface NameType {
  getName(): string;
  getField(): RuleType;
}

export function alias(name: string, rule: RuleType = null) {
  const f = rule ? rule : field();

  return {
    getName: () => name,
    getField: () => f,
  }
}

type ObjParams = { [key: string]: RuleType | NameType };

export function obj(fields: ObjParams): RuleType {
  return {
    load: (data) => {
      if (!data) {
        return data;
      }

      let result = {};

      try {
        for (let n in fields) {
          let f = fields[n];
          let name = n;

          if (f.hasOwnProperty('getName')) {
            name = (<NameType>f).getName();
            f = (<NameType>f).getField();
          }

          const value = getPathValue(data, name);
          if (!_.isUndefined(value)) {
            result[n] = (<RuleType>f).load(value);
          }
        }
      } catch (e) {
        console.error(e, data);
        throw e;
      }

      return result;
    },
    save: (data) => {
      if (!data) {
        return data;
      }

      let result = {};

      for (let n in fields) {
        let f = fields[n];
        let name = n;

        if (f.hasOwnProperty('getName')) {
          name = (<NameType>f).getName();
          f = (<NameType>f).getField();
        }

        if (n in data) {
          const value = (<RuleType>f).save(data[n]);
          setPathValue(result, name, value);
        }
      }

      return result;
    }
  }
}

export function list(field: RuleType): RuleType {
  return {
    load: (data) => {
      if (!data) {
        return data;
      }

      return data.map((d: any) => field.load(d));
    },
    save: (data) => {
      if (!data) {
        return data;
      }

      return data.map((d: any) => field.save(d));
    }
  }
}

export function date(): RuleType {
  return {
    load: (data) => {
      if (!data) {
        return data;
      }

      return parseDate(data);
    },
    save: (data) => {
      if (!data) {
        return data;
      }

      return data.toISOString().split('.')[0] + 'Z';
    }
  };
}

export function currency(): RuleType {
  return {
    load: (data) => {
      if (data === null) return null;
      return new Decimal(data).div(100);
    },
    save: (data) => {
      if (data === null) return null;
      return data.mul(100).toFixed();
    },
  }
}
